/**
 * Live updates (beta): what happens to a learner elsewhere (a friend joins the circle, an army is launched at
 * them, a raid they sent comes back) shows on the screens they already have open, with no reload.
 *
 * The screens do not poll their own data. They subscribe here, and this module asks `GET /api/raids/live` for one
 * short digest of the learner's social state (never its content). A screen's callback runs when the digest
 * changes, and once at the start in place of its own first read, so there is no gap between the first read and the
 * first digest. Plain polling, not a stream: the app is served through tunnels and proxies that hold streams, and
 * to phones on networks that cut them.
 *
 * When it checks: every `DEFAULT_MS` while the page is seen and online (a hidden tab makes no calls), at once when
 * the page is seen again, focused or back online, and when the service worker says a push arrived (`live-poke`,
 * `sw/reminders-sw.ts`), so an army at the gates shows within a moment on a page that is open. One check at a time;
 * a check asked for while one runs follows it. A callback never runs twice at once: a change that lands meanwhile
 * runs it once more afterwards. A callback that rejects is called again on the next check.
 *
 * Personal mode has no such route (404): the first answer ends the checks, and the callback still ran once.
 */
const DEFAULT_MS = 15_000;
/** A browser journey shortens the pace with `window.__eavesLiveMs`; nothing else sets it. */
const intervalMs = () => (globalThis as { __eavesLiveMs?: number }).__eavesLiveMs ?? DEFAULT_MS;

interface Sub {
  run: () => unknown;
  /** The digest this screen has read for; null until its first check, or after a failed run. */
  seen: string | null;
  started: boolean;
  running: boolean;
  again: boolean;
}

const subs = new Set<Sub>();
let latest: string | null = null;
let timer: number | undefined;
let inflight: Promise<void> | undefined;
let recheck = false;
/** The route answered 404 (personal mode): no more checks until the next screen subscribes afresh. */
let unavailable = false;

const seen = () => document.visibilityState !== 'hidden' && navigator.onLine !== false;

function kick(s: Sub) {
  if (s.running) {
    s.again = true;
    return;
  }
  s.running = true;
  void (async () => {
    try {
      do {
        s.again = false;
        try {
          await s.run();
        } catch {
          s.seen = null; // the next check calls it again
        }
      } while (s.again && subs.has(s));
    } finally {
      s.running = false;
    }
  })();
}

function deliver(ok: boolean) {
  for (const s of [...subs]) {
    if (!s.started) {
      s.started = true;
      s.seen = ok ? latest : null;
      kick(s);
    } else if (ok && latest !== s.seen) {
      s.seen = latest;
      kick(s);
    }
  }
}

function poll(): Promise<void> {
  if (!subs.size || unavailable) return Promise.resolve();
  if (inflight) {
    recheck = true;
    return inflight;
  }
  inflight = (async () => {
    let ok = false;
    try {
      const res = await fetch('/api/raids/live', { cache: 'no-store' });
      if (res.status === 404) unavailable = true;
      else if (res.ok) {
        const body = (await res.json()) as { v?: unknown };
        if (typeof body.v === 'string') {
          latest = body.v;
          ok = true;
        }
      }
    } catch {
      /* offline or the server restarting: the next check tries again */
    }
    inflight = undefined;
    deliver(ok);
    if (unavailable) stopTimer();
    if (recheck) {
      recheck = false;
      void poll();
    }
  })();
  return inflight;
}

const onSeen = () => { if (seen()) void poll(); };
const onVisibility = () => { if (document.visibilityState !== 'hidden') void poll(); };
const onMessage = (e: MessageEvent) => {
  if ((e.data as { type?: string } | null)?.type === 'live-poke') void poll();
};

function stopTimer() {
  if (timer !== undefined) window.clearInterval(timer);
  timer = undefined;
}

function start() {
  unavailable = false;
  timer = window.setInterval(() => { if (seen()) void poll(); }, intervalMs());
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onSeen);
  window.addEventListener('online', onSeen);
  if ('serviceWorker' in navigator) navigator.serviceWorker.addEventListener('message', onMessage);
}

function stop() {
  stopTimer();
  document.removeEventListener('visibilitychange', onVisibility);
  window.removeEventListener('focus', onSeen);
  window.removeEventListener('online', onSeen);
  if ('serviceWorker' in navigator) navigator.serviceWorker.removeEventListener('message', onMessage);
  latest = null;
  recheck = false;
}

/**
 * Run `run` once now (the screen's first read) and again whenever the learner's social state changes. Returns the
 * way to stop. Call it only for a learner who is signed in on a beta server (`health.beta`).
 */
export function subscribeLive(run: () => unknown): () => void {
  const s: Sub = { run, seen: null, started: false, running: false, again: false };
  if (!subs.size) start();
  subs.add(s);
  if (unavailable) {
    s.started = true;
    kick(s);
  } else void poll();
  return () => {
    if (!subs.delete(s)) return;
    if (!subs.size) stop();
  };
}
